import Cookies from 'universal-cookie';
import { API_HOST, CSRF_COOKIE_NAME } from 'src/constants/environment';

const cookies = new Cookies();

type RefreshTokenResponse = {
    access?: string;
};

type RefreshResult = {
    ok: boolean;
};

let refreshInFlight: Promise<RefreshResult> | null = null;
const refreshSession = async (): Promise<RefreshResult> => {
    try {
        const headers: Record<string, string> = {};
        const csrfToken = cookies.get(CSRF_COOKIE_NAME);
        if (typeof csrfToken === 'string' && csrfToken) {
            headers['X-CSRFToken'] = csrfToken;
        }
        const response = await fetch(`${API_HOST}/api/auth/token/refresh/`, {
            method: 'POST',
            credentials: 'include',
            headers,
        });
        if (!response.ok) {
            return { ok: false };
        }

        await response.json().catch(() => ({}) as RefreshTokenResponse);
        return { ok: true };
    } catch {
        return { ok: false };
    }
};

export const ensureFreshSession = (): Promise<RefreshResult> => {
    if (!refreshInFlight) {
        refreshInFlight = refreshSession().finally(() => {
            refreshInFlight = null;
        });
    }
    return refreshInFlight;
};

type Fetcher = (input: RequestInfo | URL, init?: RequestInit) => Promise<Response>;

export const fetchWithSessionRefresh = async (
    input: RequestInfo | URL,
    init: RequestInit = {},
    fetcher: Fetcher = fetch,
    refresh = ensureFreshSession,
): Promise<Response> => {
    const response = await fetcher(input, init);
    // One-time bearer tokens have no refresh-cookie session to renew.
    if (response.status !== 401 || new Headers(init.headers).has('Authorization')) {
        return response;
    }
    if (init.signal?.aborted) return response;
    const { ok } = await refresh();
    if (!ok || init.signal?.aborted) return response;
    return fetcher(input, init);
};
